import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import {
  FILIERE_TARIF_CATEGORIE_LABELS,
  FILIERE_TARIF_TYPE_LABELS,
  type FiliereTarifsDraft,
} from '../../../utils/filiereTarifsDraft';

type Props = {
  value: FiliereTarifsDraft;
  onChange: (next: FiliereTarifsDraft) => void;
  disabled?: boolean;
};

const fieldClass =
  'academia-field w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2 text-sm tabular-nums text-slate-900 shadow-sm transition focus:border-primary/40 focus:ring-2 focus:ring-primary/15';

export default function FiliereTarifsEditor({ value, onChange, disabled = false }: Props) {
  const updateRow = (index: number, patch: Partial<FiliereTarifsDraft['rows'][number]>) => {
    const rows = value.rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange({ ...value, rows });
  };

  return (
    <div className="space-y-4 rounded-2xl border border-outline-variant/20 bg-amber-50/40 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <LucideSymbolIcon name="payments" tone="primary" size={20} />
          </span>
          <div className="min-w-0">
            <h4 className="text-sm font-black text-slate-900">Prix de cette formation</h4>
            <p className="mt-0.5 text-xs leading-relaxed text-on-surface-variant">
              Par défaut la grille globale (Administration → Tarification) s’applique. Activez pour fixer des montants
              spécifiques à ce parcours.
            </p>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-outline-variant/20 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-outline-variant/40 text-primary focus:ring-primary/30"
            checked={value.enabled}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          />
          Tarifs personnalisés
        </label>
      </div>

      {!value.enabled ? (
        <p className="rounded-xl border border-dashed border-outline-variant/30 bg-white/80 px-4 py-3 text-sm text-on-surface-variant">
          Cette formation utilise les prix définis dans <strong>Tarification des frais</strong>.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-outline-variant/15 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Catégorie</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Montant CDF</th>
                <th className="px-3 py-2.5">Réf. USD</th>
              </tr>
            </thead>
            <tbody>
              {value.rows.map((row, index) => (
                <tr key={`${row.categorie}-${row.typeFrais}`} className="border-t border-outline-variant/10">
                  <td className="px-3 py-2 font-semibold text-slate-800">
                    {FILIERE_TARIF_CATEGORIE_LABELS[row.categorie] ?? row.categorie}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {FILIERE_TARIF_TYPE_LABELS[row.typeFrais] ?? row.typeFrais}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step="1"
                      className={fieldClass}
                      value={row.montantCdf}
                      disabled={disabled}
                      onChange={(e) => updateRow(index, { montantCdf: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      className={fieldClass}
                      value={row.referenceUsd}
                      disabled={disabled}
                      onChange={(e) => updateRow(index, { referenceUsd: e.target.value })}
                      placeholder="optionnel"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
